import type { TimeSeries } from '@piccolohealth/pbs-common';
import { ChakraV3, Select, SelectCheck, type SelectComponents } from '@piccolohealth/ui';
import { P } from '@piccolohealth/util';
import React from 'react';

import { FormStack } from '../../../../components/forms/FormStack';
import { BadgeItem } from '../../../../components/generic/BadgeItem';
import type { UseTimeseriesChartReturn } from '../../hooks/useTimeseriesChart';
import { METHOD_TYPE_OPTIONS_MAP } from '../../utils';
import { TimeSeriesAliasSquare } from './TimeSeriesAliasSquare';

interface Props {
  chart: UseTimeseriesChartReturn;
}

const components: SelectComponents<TimeSeries, never> = {
  Option: ({ option }) => {
    const method = METHOD_TYPE_OPTIONS_MAP[option.raw.source.method];

    return (
      <ChakraV3.HStack mx={-1}>
        <TimeSeriesAliasSquare alias={option.label} series={option.raw} />
        <BadgeItem label={method.label} color={method.color} />
        <ChakraV3.Spacer />
        <SelectCheck />
      </ChakraV3.HStack>
    );
  },

  Value: ({ option }) => {
    const method = METHOD_TYPE_OPTIONS_MAP[option.raw.source.method];

    return (
      <ChakraV3.HStack mx={-1}>
        <TimeSeriesAliasSquare alias={option.label} series={option.raw} />
        <BadgeItem label={method.label} color={method.color} />
      </ChakraV3.HStack>
    );
  },
};

export const AxesControl = (props: Props) => {
  const { chart } = props;

  const axisOptions = React.useMemo(() => {
    return chart.widget.series.map((s, index) => ({
      label: `${index + 1}`,
      value: s.id,
      raw: s,
      color: s.color,
    }));
  }, [chart.widget.series]);

  const leftAxisValue = !P.isNil(chart.widget.yAxes.left)
    ? axisOptions[chart.widget.yAxes.left]
    : null;

  const rightAxisValue = !P.isNil(chart.widget.yAxes.right)
    ? axisOptions[chart.widget.yAxes.right]
    : null;

  return (
    <FormStack w='full' orientation='horizontal'>
      <ChakraV3.FormField label='Left Y-Axis'>
        <Select
          size='sm'
          placeholder='Select series'
          options={axisOptions}
          value={leftAxisValue}
          onChange={(option) => chart.onSetAxisSeries('left', option?.value ?? null)}
          components={components}
        />
      </ChakraV3.FormField>
      <ChakraV3.FormField label='Right Y-Axis'>
        <Select
          size='sm'
          placeholder='Select series'
          options={axisOptions}
          value={rightAxisValue}
          onChange={(option) => chart.onSetAxisSeries('right', option?.value ?? null)}
          components={components}
        />
      </ChakraV3.FormField>
    </FormStack>
  );
};
